import { createFileRoute, Link } from "@tanstack/react-router";
import { canonical } from "@/components/site/site-config";
import { CityPageBody, LocalProofCard } from "@/components/site/CityPageBody";
import { BUSINESS } from "@/components/site/constants";
import { OWNER_SALES_TEAM, ResponsivePicture } from "@/components/site/images";
import { CRYSTAL_RIVER_BLUE_SHINGLE } from "@/components/site/project-images";

export const Route = createFileRoute("/roofing-company-crystal-river-fl")({
  head: () => ({
    meta: [
      { title: "Roofing Company in Crystal River, FL | Destination Roofing" },
      { name: "description", content: `Roof replacement, repair, metal roofing and storm-damage services in Crystal River from a licensed, family-owned local contractor. Call ${BUSINESS.phone}.` },
      { property: "og:title", content: "Roofing Company in Crystal River, FL | Destination Roofing" },
      { property: "og:description", content: "Local roofer serving Crystal River, FL. Free Roof Inspection & Estimate." },
      { property: "og:url", content: canonical("/roofing-company-crystal-river-fl") },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OWNER_SALES_TEAM.fallback },
    ],
    links: [{ rel: "canonical", href: canonical("/roofing-company-crystal-river-fl") }],
  }),
  component: () => (
    <CityPageBody
      city="Crystal River"
      slug="/roofing-company-crystal-river-fl"
      localProof={
        <LocalProofCard
          image={CRYSTAL_RIVER_BLUE_SHINGLE}
          alt="Completed blue shingle roof replacement by Destination Roofing in Crystal River Florida"
          town="Crystal River"
          title="Blue shingle roof replacement in Crystal River, FL"
          to="/projects/blue-shingle-roof-crystal-river-fl"
        />
      }
      permitNote={
        <p>
          Roof permits are handled through the Citrus County Building Division, but homes inside Crystal River city limits should confirm with the City of Crystal River. See our <Link to="/citrus-county-roof-permits">Citrus County roof permit guide</Link>.
        </p>
      }
      image={OWNER_SALES_TEAM}
      imageAlt="Destination Roofing owner and sales team in Crystal River, Florida"
      imageCaption="The Destination Roofing team serving Crystal River and Citrus County."
      intro={
        <p>
          Crystal River is beautiful — and the same coastal environment that makes it a great place to live is also hard on roofs. Salt air, humidity, and wind exposure all shorten roof lifespans compared to inland homes.
        </p>
      }
      localNotes={
        <>
          <p>
            On Crystal River homes we regularly see corrosion at metal flashings and fasteners, accelerated granule loss on shingles, and boots or sealants that fail sooner than they would inland. For waterfront and near-water homes, we often recommend upgraded flashings and, for long-term owners, metal roofing that stands up well to salt exposure.
          </p>
          <p>
            Storm exposure matters too — a coastal home takes the wind first. We build every re-roof to modern Florida wind standards and can walk you through third-nail wind mitigation, which may help you qualify for insurance credits depending on your carrier.
          </p>
        </>
      }
      recentProjectSection={
        <>
          <h2>Recent Roof Replacement in Crystal River, FL</h2>
          <p>
            Destination Roofing recently completed this shingle roof replacement in Crystal River. Real local projects like this give homeowners a better look at the roofing work we perform throughout Crystal River and Citrus County. From the initial inspection through installation and cleanup, our goal is to make the roof replacement process straightforward and well documented.
          </p>
          <figure className="my-6 rounded-xl overflow-hidden border border-border bg-white not-prose">
            <ResponsivePicture
              image={CRYSTAL_RIVER_BLUE_SHINGLE}
              alt="Completed blue shingle roof replacement by Destination Roofing in Crystal River Florida"
              sizes="(min-width: 1024px) 66vw, 100vw"
              imgClassName="w-full h-auto"
            />
            <figcaption className="p-3 text-sm text-muted-foreground">Completed shingle roof replacement in Crystal River, FL — <Link to="/projects/blue-shingle-roof-crystal-river-fl">view the blue shingle roof project</Link></figcaption>
          </figure>
          <p>
            Thinking about <Link to="/roof-replacement">replacing your roof</Link> in Crystal River? We provide free <Link to="/roof-inspection">roof inspections</Link> and estimates for <Link to="/shingle-roofing">shingle</Link>, <Link to="/metal-roofing">metal</Link> and low-slope roofing systems.
          </p>
        </>
      }
    />
  ),
});
